import React, { useState, createRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { typographyStyles, DirectionalIcon } from '@livecommerce/design-system';
import { styled } from '@linaria/react';
import { useLocalization } from 'contexts/LocalizationContext.jsx';
import OrganizationsList from 'components-v3/OrganizationsList/OrganizationsListV2.jsx';
import NavigationDropdown from 'components-v3/NavigationDropdown/index.jsx';

const { getBodyText } = typographyStyles;

import { OrgListWrapper } from './styles.js';
import { trackingService } from 'services/tracking/TrackingService.js';

const OrganizationsListOpenButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  appearance: none;
  height: 48px;
  width: 100%;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: ${(props) => (props.isOpen ? '8px 8px 0px 0px' : '8px')};
  background-color: transparent;
  border: 1px solid #F6F6F6;
  color: #F6F6F6;

  > * {
    cursor: pointer;
  }
`;

const OrgTitle = styled.label`
  width: 100%;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${getBodyText(14)};
`;

const Label = styled.label`
  margin-bottom: 4px;
  ${getBodyText(14)};
`;

const findOrganizationById = (organizations, orgId) => {
  if (!organizations || !orgId) return null;
  const org = organizations.find((org) => org.id === orgId);
  return {
    label: org?.title,
    value: org?.id,
  };
};

const OrgListSelector = ({ orgId, organizations, onSelect }) => {
  const { t } = useLocalization();

  const buttonRef = createRef();
  const orgListRef = createRef();

  const [isOrganizationSwitcherOpened, setIsOrganizationSwitcherOpened] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(findOrganizationById(organizations, orgId));

  useEffect(() => {
    const handleSizeChange = () => {
      if (!buttonRef.current) return;
      const widthButton = buttonRef.current.offsetWidth;
      if (!orgListRef.current) return;
      orgListRef.current.style.width = `${widthButton}px`;
    };

    handleSizeChange();
    window.addEventListener('resize', handleSizeChange);

    return () => {
      window.removeEventListener('resize', handleSizeChange);
    };
  }, [buttonRef, orgListRef]);

  const onSelectedItemChange = useCallback((org, e) => {
    if (!org?.id) return;
    setIsOrganizationSwitcherOpened(false);
    trackingService.track({
      eventType: `workspace-selection:${org?.id === selectedOrganization?.value ? 'same' : 'different'}`,
    });
    setSelectedOrganization(findOrganizationById(organizations, org?.id));
    onSelect(org);
  }, [organizations, selectedOrganization, onSelect]);

  const handleOrganizationSwitcherClickOutside = () => {
    setIsOrganizationSwitcherOpened(false);
    trackingService.track({
      eventType: 'workspace-list:close',
    });
  };

  const toggleList = useCallback(() => {
    if (!organizations || organizations.length === 1) return;
    setIsOrganizationSwitcherOpened((prevValue) => {
      trackingService.track({
        eventType: `workspace-list:${prevValue ? 'close' : 'open'}`,
      });
      return !prevValue;
    });
  }, [organizations]);

  return (
    <OrgListWrapper>
      <Label>{t('genericLabels.workspace')}</Label>
      <NavigationDropdown
        isOpened={isOrganizationSwitcherOpened}
        onClickOutside={handleOrganizationSwitcherClickOutside}
        isControlled
        hoverBackgroundColor="transparent"
        dropdownGap={0}
        dropdownBorderRadius="0px 0px 8px 8px"
        dropdownPosition="bottom"
        dropdownAlignment="start"
        dropdownContent={
          <OrganizationsList
            isOpen={isOrganizationSwitcherOpened}
            handleMenuItemClick={onSelectedItemChange}
            organizations={organizations}
            ref={orgListRef}
          />
        }
      >
        <OrganizationsListOpenButton onClick={toggleList} isOpen={isOrganizationSwitcherOpened} ref={buttonRef}>
          <OrgTitle>{selectedOrganization?.label || t('genericLabels.selectWorkspace')}</OrgTitle>
          <DirectionalIcon name="chevron" direction={isOrganizationSwitcherOpened ? 'up' : 'down'} />
        </OrganizationsListOpenButton>
      </NavigationDropdown>
    </OrgListWrapper>
  );
};

OrgListSelector.propTypes = {
  organizations: PropTypes.array,
  onSelect: PropTypes.func.isRequired,
};

export default OrgListSelector;
